import React, { useState, useEffect } from 'react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import AfipConfigModal from '../components/modals/AfipConfigModal';
import { Save, Moon, Sun, Calculator } from 'lucide-react';
import { useAppStore } from '../store';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useToast } from '../hooks/useToast';

const Settings: React.FC = () => {
  const [formData, setFormData] = useState({
    companyName: 'Productex',
    taxId: '30-71234567-9',
    email: 'admin@productex.com',
    phone: '+54 11 4567-8901',
    address: 'Av. Córdoba 1234, CABA, Argentina',
    fiscalYear: '2025',
  });

  const [expressCosts, setExpressCosts] = useState({
    remeraCost: '0',
    buzoCost: '0',
  });

  const [isAfipModalOpen, setIsAfipModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { darkMode, toggleDarkMode } = useAppStore();
  const navigate = useNavigate();
  const { toast, ToastComponent } = useToast();
  
  // Fetch express costs on component mount
  useEffect(() => {
    const fetchExpressCosts = async () => {
      try {
        const { data, error } = await supabase
          .from('express_costs')
          .select('*')
          .limit(1);
        
        if (error) throw error;
        
        if (data && data.length > 0) {
          setExpressCosts({
            remeraCost: data[0].remera_cost.toString(),
            buzoCost: data[0].buzo_cost.toString(),
          });
        }
      } catch (error) {
        console.error('Error fetching express costs:', error);
      }
    };
    
    fetchExpressCosts();
  }, []);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  
  const handleExpressCostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setExpressCosts({
      ...expressCosts,
      [name]: value,
    });
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Saved settings:', formData);
  };

  const handleAfipConfig = (config: any) => {
    console.log('AFIP config:', config);
  };

  const handleGoToQuoteInputs = () => {
    navigate('/configuracion/cotizador');
  };
  
  const handleSaveExpressCosts = async () => {
    setIsLoading(true);
    try {
      // Convert string values to numbers
      const remeraCost = parseFloat(expressCosts.remeraCost) || 0;
      const buzoCost = parseFloat(expressCosts.buzoCost) || 0;
      
      // Check if record exists
      const { data, error: checkError } = await supabase
        .from('express_costs')
        .select('id')
        .limit(1);
      
      if (checkError) throw checkError;
      
      if (data && data.length > 0) {
        // Update existing record
        const { error } = await supabase
          .from('express_costs')
          .update({
            remera_cost: remeraCost,
            buzo_cost: buzoCost,
            updated_at: new Date().toISOString()
          })
          .eq('id', data[0].id);
        
        if (error) throw error;
      } else {
        // Insert new record
        const { error } = await supabase
          .from('express_costs')
          .insert([{
            remera_cost: remeraCost,
            buzo_cost: buzoCost
          }]);
        
        if (error) throw error;
      }
      
      // Log activity
      await supabase
        .from('activity_logs')
        .insert([{
          user_id: (await supabase.auth.getUser()).data.user?.id,
          action: 'Actualización de costos Express',
          entity_type: 'express_costs',
          entity_id: null,
          details: `Costos actualizados: Remera $${remeraCost}, Buzo $${buzoCost}`,
          action_type: 'update'
        }]);
      
      toast.success('Costos Express actualizados', 'Los costos han sido actualizados correctamente');
    } catch (error: any) {
      console.error('Error saving express costs:', error);
      toast.error('Error al guardar', error.message || 'Ha ocurrido un error al guardar los costos');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader title="Configuración de la Empresa" />
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Nombre de la Empresa"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                required
              />
              
              <Input
                label="CUIT"
                id="taxId"
                name="taxId"
                value={formData.taxId}
                onChange={handleInputChange}
                required
              />
              
              <Input
                label="Email"
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
              
              <Input
                label="Teléfono"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
              />
              
              <Input
                label="Dirección"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
                className="md:col-span-2"
              />
              
              <Input
                label="Año Fiscal"
                id="fiscalYear"
                name="fiscalYear"
                value={formData.fiscalYear}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="mt-6 flex justify-end">
              <Button 
                type="submit"
                icon={<Save className="h-5 w-5" />}
              >
                Guardar Cambios
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader title="Costos Express" />
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Configure los costos base para productos Express. Estos valores se utilizarán para calcular el margen bruto en el dashboard.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Costo Base Remera"
                id="remeraCost"
                name="remeraCost"
                type="number"
                value={expressCosts.remeraCost}
                onChange={handleExpressCostChange}
                required
                min="0"
                step="0.01"
              />
              
              <Input
                label="Costo Base Buzo"
                id="buzoCost"
                name="buzoCost"
                type="number"
                value={expressCosts.buzoCost}
                onChange={handleExpressCostChange}
                required
                min="0"
                step="0.01"
              />
            </div>
            
            <div className="mt-6 flex justify-end">
              <Button 
                onClick={handleSaveExpressCosts}
                icon={<Save className="h-5 w-5" />}
                disabled={isLoading}
              >
                {isLoading ? 'Guardando...' : 'Guardar Costos Express'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader title="Integración AFIP" />
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Configure la integración con AFIP para la facturación electrónica.
              </p>
              <Button 
                variant="outline" 
                className="w-full justify-center"
                onClick={() => setIsAfipModalOpen(true)}
              >
                Configurar AFIP
              </Button>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader title="Preferencias" />
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">Notificaciones</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">Modo Oscuro</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    checked={darkMode}
                    onChange={toggleDarkMode}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                  <span className="ml-2">
                    {darkMode ? <Moon className="h-4 w-4 text-gray-300" /> : <Sun className="h-4 w-4 text-gray-700" />}
                  </span>
                </label>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">Alertas de saldo bajo</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                </label>
              </div>
              
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="outline"
                  className="w-full justify-center"
                  icon={<Calculator className="h-5 w-5" />}
                  onClick={handleGoToQuoteInputs}
                >
                  Configuración del Cotizador
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <AfipConfigModal
        isOpen={isAfipModalOpen}
        onClose={() => setIsAfipModalOpen(false)}
        onSave={handleAfipConfig}
      />
      
      {/* Toast Component */}
      {ToastComponent}
    </div>
  );
};

export default Settings;